import React from 'react';

import { css, cx } from '@emotion/css';
import {
  Card,
  CardContent,
  CardHeader,
  Theme,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { FontSizes } from '@/assets/fonts';
import { Icons } from '@/assets/icons';
import { MenuTooltip } from '@/components/floating/MenuTooltip';
import { useStyles } from '@/hooks/useStyles';
import { Patient } from '@/models/PatientModel';
import { userFormattedName } from '@/models/UserModel.ts';
import { inseeTownshipToString } from '@/models/identity/FrenchIdentity';
import { Queries } from '@/queries/Queries';
import { DateUtils } from '@/utils/dateUtils.ts';

type PatientProps = {
  patientInformation: Patient;
};

type PatientProfileProps = {
  patientId: string;
};

export const PatientProfile: React.FC<PatientProfileProps> = ({
  patientId,
}) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  const patient = Queries.practitioner.usePatient(patientId);

  return (
    <Card elevation={0}>
      <CardHeader title={t('pages.account.generalInformation.title')} />
      <CardContent className={styles.body}>
        <div className={styles.content}>
          <div className={styles.multiCol}>
            <div className={styles.column}>
              <NameInfo patientInformation={patient} />
              <SexInfo patientInformation={patient} />
              <BirthInfo patientInformation={patient} />
            </div>
            <div className={styles.column}>
              <PhoneInfo patientInformation={patient} />
              <EmailInfo patientInformation={patient} />
              <AddressInfo patientInformation={patient} />
            </div>
          </div>
          <MenuTooltip
            placement="bottom-start"
            openOnHover
            className={styles.moreInfo}
            content={
              <div className={cx(styles.multiCol, styles.tooltip)}>
                <div className={styles.column}>
                  <Typography variant="body">{`${t('profile.usedFirstname')} : ${
                    patient.givenName ?? t('profile.notGiven')
                  }`}</Typography>
                  <Typography variant="body">{`${t('profile.usedLastname')} : ${
                    patient.familyName ?? t('profile.notGiven')
                  }`}</Typography>
                  <Typography variant="body">{`${t(
                    'profile.identityFirstnames',
                  )} : ${
                    patient.identityFirstnames.length > 0
                      ? patient.identityFirstnames.join(', ')
                      : t('profile.notGiven')
                  }`}</Typography>
                  <Typography variant="body">{`${t('profile.identityLastname')} : ${
                    patient.identityLastname ?? t('profile.notGiven')
                  }`}</Typography>
                  <Typography variant="body">{`${t('profile.gender')} : ${
                    patient.gender == 'male'
                      ? t('profile.male')
                      : patient.gender == 'female'
                        ? t('profile.female')
                        : t('profile.notGiven')
                  }`}</Typography>
                </div>
                <div className={styles.column}>
                  <BirthDateDetailsInfo patientInformation={patient} />
                  <BirthLocationDetailsInfo patientInformation={patient} />
                  <Typography variant="body">{`${t('profile.ins')} : ${
                    patient.ins ? patient.ins : t('profile.notGiven')
                  }`}</Typography>
                  <Typography variant="body">{`${t('profile.oid')} : ${
                    patient.oid ? patient.oid : t('profile.notGiven')
                  }`}</Typography>
                  <Typography variant="body">{`${t('profile.ssn')} : ${
                    patient.socialSecurityNumber ?? t('profile.notGiven')
                  }`}</Typography>
                </div>
              </div>
            }
          >
            <div className={styles.itemRow}>
              <Typography variant="body">{t('profile.moreInfo')}</Typography>
              <Icons.chevronDown className={styles.icon} />
            </div>
          </MenuTooltip>
        </div>
      </CardContent>
    </Card>
  );
};

const NameInfo: React.FC<PatientProps> = ({ patientInformation }) => {
  const styles = useStyles(makeStyles);

  return (
    <div className={styles.itemRow}>
      <Typography variant="body">
        {userFormattedName(
          patientInformation.givenName,
          patientInformation.familyName,
        )}
      </Typography>
    </div>
  );
};

const SexInfo: React.FC<PatientProps> = ({ patientInformation }) => {
  const { t } = useTranslation();

  if (!patientInformation.gender) {
    return null;
  }
  return (
    <Typography variant="body">
      {patientInformation.gender == 'male'
        ? t('profile.male')
        : t('profile.female')}
    </Typography>
  );
};

const BirthInfo: React.FC<PatientProps> = ({ patientInformation }) => {
  const { t } = useTranslation();

  if (!patientInformation.birthDate || !patientInformation.birthCountry) {
    return null;
  }
  const age = DateUtils.age(patientInformation.birthDate);
  const birthLocation = patientInformation.birthLocation
    ? `${t('patientInfo.placeOfBirth', {
        context: patientInformation.gender,
      })} ${inseeTownshipToString(patientInformation.birthLocation)}, ${
        patientInformation.birthCountry
      }`
    : `${t('patientInfo.placeOfBirth', {
        context: patientInformation.gender,
      })} ${patientInformation.birthCountry}`;

  return (
    <Typography variant="body">
      {`${t('pages.patientMonitoring.patient.age', {
        age,
      })}, ${birthLocation}`}
    </Typography>
  );
};

const PhoneInfo: React.FC<PatientProps> = ({ patientInformation }) => {
  const styles = useStyles(makeStyles);

  if (!patientInformation.contactPhone) {
    return null;
  }
  return (
    <div className={styles.itemRow}>
      <Icons.phone className={styles.icon} />
      <Typography variant="body">{patientInformation.contactPhone}</Typography>
    </div>
  );
};

const EmailInfo: React.FC<PatientProps> = ({ patientInformation }) => {
  const styles = useStyles(makeStyles);

  return (
    <div className={styles.itemRow}>
      <Icons.email className={styles.icon} />
      <Typography variant="body">{patientInformation.email}</Typography>
    </div>
  );
};

const AddressInfo: React.FC<PatientProps> = ({ patientInformation }) => {
  const styles = useStyles(makeStyles);
  if (
    !patientInformation.addressLine ||
    !patientInformation.addressPostalCode ||
    !patientInformation.addressCity
  ) {
    return null;
  }

  return (
    <div className={styles.itemRow}>
      <Icons.mail className={styles.icon} />
      <Typography variant="body">
        {`${patientInformation.addressLine}, ${patientInformation.addressPostalCode} ${patientInformation.addressCity}`}
      </Typography>
    </div>
  );
};

const BirthDateDetailsInfo: React.FC<PatientProps> = ({
  patientInformation,
}) => {
  const { t } = useTranslation();
  if (!patientInformation.birthDate) {
    return (
      <Typography variant="body">{`${t('profile.birthDate')} : ${t(
        'profile.notGiven',
      )}`}</Typography>
    );
  }
  const age = DateUtils.age(patientInformation.birthDate);
  const birthDate = patientInformation.birthDate.toLocaleString();
  const ageText = `(${t('pages.patientMonitoring.patient.age', {
    age,
  })})`;

  return (
    <Typography variant="body">{`${t(
      'profile.birthDate',
    )} : ${birthDate} ${ageText}`}</Typography>
  );
};

const BirthLocationDetailsInfo: React.FC<PatientProps> = ({
  patientInformation,
}) => {
  const { t } = useTranslation();
  if (!patientInformation.birthCountry) {
    return (
      <Typography variant="body">{`${t('profile.birthPlace')} : ${t(
        'profile.notGiven',
      )}`}</Typography>
    );
  }
  const birthLocation = patientInformation.birthLocation
    ? `${t('patientInfo.placeOfBirth', {
        context: patientInformation.gender,
      })} ${inseeTownshipToString(patientInformation.birthLocation)}, ${
        patientInformation.birthCountry
      }`
    : `${t('patientInfo.countryOfBirth', {
        context: patientInformation.gender,
      })} ${patientInformation.birthCountry}`;

  return (
    <Typography variant="body">{`${t(
      'profile.birthPlace',
    )} : ${birthLocation}`}</Typography>
  );
};

const makeStyles = (theme: Theme) => ({
  body: css``,
  content: css`
    display: flex;
    flex-direction: column;
    width: 100%;
  `,
  multiCol: css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(30)};
  `,
  column: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(4)};
  `,
  itemRow: css`
    display: flex;
    flex-direction: row;
    align-items: center;
  `,
  moreInfo: css`
    margin-top: ${theme.spacing(12)};
    width: fit-content;
  `,
  tooltip: css`
    margin: ${theme.spacing(12)};
  `,
  icon: css`
    width: ${FontSizes.body};
    height: ${FontSizes.body};
    margin: 0 ${theme.spacing(4)};
  `,
});
